<script setup lang="ts">
import { ref, computed, toRaw } from 'vue'
import type { StepperItem } from '@nuxt/ui'
import { useAuthStore } from '@/stores/auth'

interface FormRefInstance {
  validate: (opts?: { name?: string | string[]; silent?: boolean }) => Promise<unknown>
}

const props = withDefaults(
  defineProps<{
    items: StepperItem[]
    schema?: unknown
    state: Record<string, unknown>
    stepFields?: string[][]
    submitting?: boolean
    showVisibilityHint?: boolean
    submitButtonText?: string
    submitButtonColor?: 'primary' | 'error' | 'success' | 'warning' | 'neutral'
    submitButtonIcon?: string
  }>(),
  {
    schema: undefined,
    stepFields: () => [],
    submitting: false,
    showVisibilityHint: false,
    submitButtonText: 'Meldung absenden',
    submitButtonColor: 'primary',
    submitButtonIcon: 'i-lucide-send'
  }
)

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'back'): void
}>()

const authStore = useAuthStore()
const activeStep = ref(0)
const formRef = ref<FormRefInstance | null>(null)

const isFirstStep = computed(() => activeStep.value === 0)
const isLastStep = computed(() => activeStep.value === props.items.length - 1)
const isMissingFullName = computed(() => !authStore.userInfo?.user?.fullName)

async function goNext() {
  const fields = props.stepFields[activeStep.value]
  if (fields && fields.length > 0 && formRef.value) {
    try {
      await formRef.value.validate({ name: fields, silent: false })
    } catch {
      return
    }
  }
  if (!isLastStep.value) {
    activeStep.value++
  }
}

function goBack() {
  if (isFirstStep.value) {
    emit('back')
  } else {
    activeStep.value--
  }
}

function handleSubmit() {
  emit('submit')
}
</script>

<template>
  <div class="space-y-6">
    <UStepper v-model="activeStep" :items="items" class="w-full" />

    <UAlert
      v-if="isMissingFullName"
      color="error"
      variant="subtle"
      title="Name im Benutzerkonto fehlt"
      icon="i-lucide-alert-triangle"
      description="In Ihrem Benutzerkonto ist kein vollständiger Name hinterlegt. Das Absenden von Meldungen ist blockiert. Bitte wenden Sie sich an Ihren Administrator."
    />

    <UAlert
      v-if="showVisibilityHint"
      color="info"
      variant="subtle"
      title="Hinweis zur Sichtbarkeit"
      icon="i-lucide-lock"
      description="Eingereichte Tickets sind zunächst nur für Mitarbeiter Ihres Unternehmens sichtbar. MBCOM kann das Ticket bei Bedarf für alle Kunden freischalten."
    />

    <UForm
      ref="formRef"
      :schema="toRaw(schema)"
      :state="state"
      class="space-y-6"
      @submit="handleSubmit"
    >
      <slot :step="activeStep" />

      <div class="flex justify-between pt-6 border-t border-neutral-200 dark:border-neutral-800">
        <UButton
          type="button"
          color="neutral"
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="goBack"
        >
          Zurück
        </UButton>

        <UButton
          v-if="!isLastStep"
          type="button"
          color="primary"
          trailing-icon="i-lucide-arrow-right"
          @click="goNext"
        >
          Weiter
        </UButton>

        <UButton
          v-else
          type="submit"
          :color="submitButtonColor"
          :loading="submitting"
          :disabled="isMissingFullName"
          :icon="submitButtonIcon"
        >
          {{ submitButtonText }}
        </UButton>
      </div>
    </UForm>
  </div>
</template>
